import React, { ReactElement, ReactNode, useContext } from 'react'
import { Checkbox, Input, MenuItem, Select as SelectMUI } from '@material-ui/core'
import {
    Controller,
    ControllerRenderProps,
    useFormContext,
    UseFormMethods
} from '../../../../../Vendor/react-hook-form/dist/index.ie11'
import Label from '../../Label'
import { stripHTML } from '../../../../../NonReact/utils/HTMLUtils'
import { BaseSelectProps, SelectOptionValues } from '../Types'
import { CommonFormContext, FormContextType } from '../../../Contexts/CommonFormContext'
import { isStringNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'

export type OptionProps<T extends SelectOptionValues> = {
    label: string
    value: T
    renderOptionElement?: ReactElement
}[]

export type SelectMultiProps<T extends SelectOptionValues> = BaseSelectProps<T> & {
    name?: string
    label?: ReactNode
    value?: T[]
    handleChange: (value: T[]) => void
    options: OptionProps<T>
    renderSelectedValue?: boolean
    nativeTransitionIndex?: number
    nativeTransition?: boolean
    className?: string
    variant?: 'standard' | 'outlined' | 'filled'
}

/**
 *
 * @param label Label for select box.
 * @param value List of currently selected values.
 * @param handleChange Method to handle the change event of the select dropdown.
 * @param options Property containing selection options.
 * @param renderSelectedValue A flag specifying that selected option value should be rendered instead of selected option label.
 * @param disabled A flag defining the disabled state of the select component.
 * @param nativeTransitionIndex This is the options array length at which the transition to native occurs.
 * @param nativeTransition DEFAULT TO FALSE, this determines whether or not we transition to native
 *
 */
const SelectMulti = <T extends SelectOptionValues>({
    name,
    label,
    options,
    value,
    handleChange,
    renderSelectedValue,
    disabled,
    nativeTransitionIndex,
    nativeTransition,
    className,
    variant
}: SelectMultiProps<T>): ReactElement => {
    // eslint-disable-next-line
    let methods: Definable<UseFormMethods<Record<string, any>>>
    if (name) {
        methods = useFormContext()
    }

    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const handleOnChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
        if (commonForm?.handleFormChange) {
            commonForm?.handleFormChange(name)
        }
        const newValue = event.target.value as T[]
        handleChange(newValue)
    }

    /**
     * Separate onChange handler for native select components, since the selected values are in different event property.
     * @param event Event sent from native select element.
     */
    const handleNativeOnChange = (event: React.ChangeEvent<{ value: unknown; selectedOptions?: unknown }>): void => {
        if (commonForm?.handleFormChange) {
            commonForm?.handleFormChange(name)
        }
        const selectedOptions: T[] = Array.from(
            event.target.selectedOptions as Iterable<{ value: T }>,
            (option) => option.value
        )
        handleChange(selectedOptions)
    }

    // Define custom renderValue function.
    const renderValue = renderSelectedValue
        ? (selected: unknown): string => (selected as string[]).join(', ')
        : (selected: unknown): string => {
              const selectedValues: string[] = selected as string[]
              const selectedOptions = selectedValues.map((selectedValue) =>
                  options.find((option) => option.value === selectedValue)
              )
              if (selectedOptions) return selectedOptions.map((selectedOption) => selectedOption?.label).join(', ')
              return ''
          }

    // Transition to native select behaviour, defaulted to 200.
    const isNative: boolean = nativeTransition ? options.length > (nativeTransitionIndex ?? 200) : false

    // Create the options list for the select box, either native or non-native.
    const generateOptions = (selectedValues?: T[]): ReactElement[] => {
        if (isNative) {
            return options.map((option) => (
                <option key={option.value} value={option.value}>
                    {stripHTML(option.label)}
                </option>
            ))
        }

        return options.map((option) => (
            <MenuItem key={option.label} value={option.value}>
                <Checkbox checked={selectedValues && selectedValues.indexOf(option.value) > -1} />
                {option.renderOptionElement ? (
                    option.renderOptionElement
                ) : (
                    <span className='option-wrapper' dangerouslySetInnerHTML={{ __html: option.label }} />
                )}
            </MenuItem>
        ))
    }

    return (
        <div className={`select-multi-wrapper ${isStringNotEmpty(className) ? className : EMPTY_STRING}`}>
            <div className='select-input'>
                {label && <Label>{label}</Label>}
                <div className='input-wrapper'>
                    {methods ? (
                        <Controller
                            control={methods.control}
                            name={name ?? ''}
                            defaultValue={methods?.watch(name ?? '', [])}
                            render={({
                                value: valueForm,
                                name: nameForm,
                                ref
                            }: ControllerRenderProps<Record<string, unknown>>): ReactElement => (
                                <SelectMUI
                                    name={nameForm}
                                    inputRef={ref}
                                    multiple
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                    value={valueForm}
                                    onChange={isNative ? handleNativeOnChange : handleOnChange}
                                    input={<Input />}
                                    renderValue={renderValue}
                                    native={isNative}
                                    disabled={!!disabled}
                                    variant={variant}
                                >
                                    {generateOptions(valueForm as T[])}
                                </SelectMUI>
                            )}
                        />
                    ) : (
                        <SelectMUI
                            multiple
                            value={value}
                            onChange={isNative ? handleNativeOnChange : handleOnChange}
                            input={<Input />}
                            renderValue={renderValue}
                            native={isNative}
                            disabled={!!disabled}
                            variant={variant}
                        >
                            {generateOptions(value)}
                        </SelectMUI>
                    )}
                </div>
            </div>
        </div>
    )
}
export default SelectMulti
